<script setup lang="ts">
import type { Course } from '~~/shared/types/course'

const props = withDefaults(defineProps<{
  course: Course
  registrationBase?: string
  showRegistration?: boolean
}>(), {
  showRegistration: true
})

function formatDate(dateStr: string): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('de-CH', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

function formatTime(dateStr: string): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })
}

const teacherNames = computed(() =>
  props.course.teachers
    .map(t => `${t.dossier?.firstname ?? ''} ${t.dossier?.lastname ?? ''}`.trim())
    .filter(Boolean)
    .join(', ')
)

const priceDisplay = computed(() => {
  if (props.course.price_text) return props.course.price_text
  if (props.course.price_chf) {
    const amount = Number(props.course.price_chf)
    return Number.isNaN(amount) ? null : `CHF ${amount.toFixed(2)}`
  }
  return null
})

// Bei vielen Terminen (Lehrgänge) die Sessionliste einklappbar machen,
// kurze Kurse bleiben vollständig sichtbar.
const SESSION_COLLAPSE_THRESHOLD = 6
const collapsibleSessions = computed(() => (props.course.sessions?.length ?? 0) > SESSION_COLLAPSE_THRESHOLD)
const sessionsExpanded = ref(false)
</script>

<template>
  <UCard class="flex flex-col gap-2">
    <template #header>
      <div class="flex items-start justify-between gap-2">
        <h3 class="font-semibold text-base leading-tight">
          {{ course.title }}
        </h3>
        <UBadge
          v-if="course.is_confirmed"
          color="success"
          variant="subtle"
          class="shrink-0"
        >
          Bestätigt
        </UBadge>
      </div>
    </template>

    <div class="flex flex-col gap-1 text-sm text-gray-600 dark:text-gray-400">
      <div
        v-if="course.startdate"
        class="flex items-center gap-1"
      >
        <UIcon
          name="i-lucide-calendar"
          class="shrink-0"
        />
        <span>{{ formatDate(course.startdate) }}
          <template v-if="course.enddate && course.enddate !== course.startdate">
            – {{ formatDate(course.enddate) }}
          </template>
        </span>
      </div>

      <div
        v-if="course.sessions?.length === 1"
        class="flex items-center gap-1"
      >
        <UIcon
          name="i-lucide-clock"
          class="shrink-0"
        />
        <span>{{ formatTime(course.sessions[0].startdatetime) }} – {{ formatTime(course.sessions[0].enddatetime) }} Uhr</span>
      </div>

      <div
        v-else-if="course.sessions?.length"
        class="flex items-start gap-1"
      >
        <UIcon
          name="i-lucide-clock"
          class="mt-0.5 shrink-0"
        />
        <div class="flex flex-col items-start gap-1">
          <ul
            v-if="!collapsibleSessions || sessionsExpanded"
            class="flex flex-col gap-0.5"
          >
            <li
              v-for="session in course.sessions"
              :key="session.id"
            >
              {{ formatDate(session.startdatetime) }},
              {{ formatTime(session.startdatetime) }} – {{ formatTime(session.enddatetime) }}
              <span
                v-if="session.module?.title"
                class="text-gray-400"
              >({{ session.module?.title }})</span>
            </li>
          </ul>
          <UButton
            v-if="collapsibleSessions"
            variant="link"
            color="neutral"
            size="xs"
            class="px-0"
            :trailing-icon="sessionsExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
            :label="sessionsExpanded ? 'Termine ausblenden' : `Alle ${course.sessions.length} Termine anzeigen`"
            @click="sessionsExpanded = !sessionsExpanded"
          />
        </div>
      </div>

      <div
        v-if="course.totalLessons"
        class="flex items-center gap-1"
      >
        <UIcon
          name="i-lucide-book-open"
          class="shrink-0"
        />
        <span>{{ course.totalLessons }} Lektionen</span>
      </div>

      <div
        v-if="teacherNames"
        class="flex items-center gap-1"
      >
        <UIcon
          name="i-lucide-user"
          class="shrink-0"
        />
        <span>{{ teacherNames }}</span>
      </div>
      <!--
      <div
        v-if="course.remote_option"
        class="flex items-center gap-1"
      >
        <UIcon
          name="i-lucide-monitor"
          class="shrink-0"
        />
        <span>{{ course.remote_option }}</span>
      </div>
      -->
    </div>

    <template #footer>
      <div class="flex items-center justify-between gap-2">
        <span
          v-if="priceDisplay"
          class="font-semibold text-primary whitespace-pre-line"
        >{{ priceDisplay }}</span>
        <span v-else />
        <UButton
          v-if="showRegistration"
          :to="`${props.registrationBase ?? '/anmeldung'}?kurs=${course.id}`"
          size="sm"
          trailing-icon="i-lucide-arrow-right"
        >
          Anmelden
        </UButton>
      </div>
    </template>
  </UCard>
</template>
